if (!customElements.get('product-information-drawer')) {
  customElements.define(
    'product-information-drawer',
    class ProductInformationDrawer extends HTMLElement {
      static activeDrawer = null;

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.dialog = this.querySelector('dialog');
        this.panel = this.querySelector('.product-information-drawer__panel');
        this.body = this.querySelector('[data-product-information-body]');
        this.heading = this.querySelector('.product-information-drawer__heading');
        if (!this.productInfo || !this.dialog || typeof this.dialog.showModal !== 'function') return;

        this.events?.abort();
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.productInfo.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-product-information-trigger]');
          if (!trigger || event.defaultPrevented || event.button !== 0) return;
          if (this.openFor(trigger.closest('[data-product-information-item]'), trigger)) event.preventDefault();
        }, options);
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog || event.target.closest('[data-product-information-close]')) this.close();
        }, options);
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        }, options);
        this.dialog.addEventListener('close', () => {
          if (!this.dialog.open) this.restoreContent();
        }, options);
        this.dialog.addEventListener('keydown', (event) => this.trapKeyboardFocus(event), options);
        this.productInfo.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest('[data-product-information-item]');
          if (item) this.openFor(item, item.querySelector('[data-product-information-trigger]'));
        }, options);
        document.addEventListener('cart-drawer:opened', () => this.finishClose(), options);
        this.productInfo.querySelectorAll('[data-product-information-trigger]').forEach((trigger) => {
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-controls', this.dialog.id);
          trigger.setAttribute('aria-expanded', 'false');
        });
        this.productInfo.querySelectorAll('[data-product-description-shortcut]').forEach((shortcut) => {
          if (!this.productInfo.querySelector(shortcut.hash)?.matches('[data-product-information-item]')) return;
          shortcut.setAttribute('aria-haspopup', 'dialog');
          shortcut.setAttribute('aria-controls', this.dialog.id);
          shortcut.setAttribute('aria-expanded', 'false');
        });
      }

      disconnectedCallback() {
        this.events?.abort();
        this.finishClose(false);
      }

      openFor(item, trigger) {
        if (!item?.matches('[data-product-information-item]') || !this.productInfo.contains(item)) return false;
        if (this.dialog?.open && this.item === item) {
          this.querySelector('[data-product-information-close]').focus({ preventScroll: true });
          return true;
        }
        const content = item.querySelector('[data-product-information-content]');
        if (!content || !this.isConnected || typeof this.dialog?.showModal !== 'function') return false;

        this.constructor.activeDrawer?.finishClose(false);
        this.constructor.activeDrawer = this;
        this.item = item;
        this.trigger = trigger;
        this.content = content;
        this.contentParent = content.parentNode;
        this.contentNext = content.nextSibling;
        this.heading.textContent = item.querySelector('.product-information-row__title').textContent.trim();
        item.open = false;
        this.body.append(content);
        this.body.scrollTop = 0;
        this.scrollPosition = window.scrollY;
        document.documentElement.classList.toggle(
          'product-information-scrollbar-gutter',
          window.innerWidth > document.documentElement.clientWidth
        );
        document.documentElement.classList.add('product-information-scroll-locked');
        document.body.classList.add('product-information-scroll-locked');
        this.dialog.classList.remove('is-closing');
        this.dialog.showModal();
        this.dialog.classList.add('is-open');
        const animation = this.animatePanel(
          this.offscreenTransform,
          'translate3d(0, 0, 0)',
          'cubic-bezier(0.22, 1, 0.36, 1)'
        );
        if (animation) {
          animation.finished.then(() => {
            if (this.panelAnimation !== animation) return;
            animation.cancel();
            this.panelAnimation = null;
          }).catch(() => {});
        }
        item.querySelector('[data-product-information-trigger]').setAttribute('aria-expanded', 'true');
        this.trigger?.setAttribute('aria-expanded', 'true');
        this.querySelector('[data-product-information-close]').focus({ preventScroll: true });
        return true;
      }

      close() {
        if (!this.dialog.open || this.closeTimer) return;
        const currentTransform = window.getComputedStyle(this.panel).transform || 'none';
        this.dialog.classList.add('is-closing');
        this.dialog.classList.remove('is-open');
        const animation = this.animatePanel(
          currentTransform,
          this.offscreenTransform,
          'cubic-bezier(0.4, 0, 1, 1)'
        );
        if (!animation) {
          this.finishClose();
          return;
        }
        this.closeTimer = window.setTimeout(() => this.finishClose(), this.motionDuration + 50);
        animation.finished.then(() => {
          if (this.panelAnimation === animation) this.finishClose();
        }).catch(() => {});
      }

      get motionDuration() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
        return parseFloat(window.getComputedStyle(this.dialog).getPropertyValue('--product-information-drawer-duration')) || 350;
      }

      get offscreenTransform() {
        const inset = parseFloat(window.getComputedStyle(this.dialog).getPropertyValue('--product-information-drawer-inset')) || 16;
        return `translate3d(${this.panel.getBoundingClientRect().width + inset}px, 0, 0)`;
      }

      animatePanel(from, to, easing) {
        this.panelAnimation?.cancel();
        this.panelAnimation = null;
        const duration = this.motionDuration;
        if (!duration || typeof this.panel.animate !== 'function') return null;
        this.panelAnimation = this.panel.animate([{ transform: from }, { transform: to }], {
          duration,
          easing,
          fill: 'both',
        });
        return this.panelAnimation;
      }

      finishClose(restoreFocus = true) {
        this.panelAnimation?.cancel();
        this.panelAnimation = null;
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
        if (this.dialog?.open) this.dialog.close();
        this.restoreContent(restoreFocus);
      }

      restoreContent(restoreFocus = true) {
        this.panelAnimation?.cancel();
        this.panelAnimation = null;
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
        if (!this.content) return;
        const trigger = this.trigger;
        this.contentParent.insertBefore(this.content, this.contentNext);
        this.item.querySelector('[data-product-information-trigger]').setAttribute('aria-expanded', 'false');
        trigger?.setAttribute('aria-expanded', 'false');
        this.content = null;
        this.dialog.classList.remove('is-closing', 'is-open');
        if (this.constructor.activeDrawer === this) {
          this.constructor.activeDrawer = null;
          document.documentElement.classList.remove('product-information-scroll-locked', 'product-information-scrollbar-gutter');
          document.body.classList.remove('product-information-scroll-locked');
          window.scrollTo({ top: this.scrollPosition, behavior: 'instant' });
        }
        if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
      }

      trapKeyboardFocus(event) {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        )).filter((element) => element.getClientRects().length && !element.closest('[hidden], [inert]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
